import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Building2,
  MapPin,
  DollarSign,
  Calendar,
  Briefcase,
  Search,
  Download,
  UserCircle,
  Mail,
  Phone,
  ExternalLink
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

export function ApplicationsSection() {
  const navigate = useNavigate();
  const { applications, jobs, currentUser } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const userApplications = applications?.filter(app => {
    const appUserId = String(app.userId || '').trim();
    const currentId = String(currentUser?.id || '').trim();
    return appUserId && currentId && appUserId === currentId;
  }) || [];

  console.log('📊 User applications:', userApplications.length, 'Current user:', currentUser?.id);

  // Get applications with job data
  const applicationsWithJobs = userApplications.map(app => {
    // Handle both job object (from API) and jobId (from local state)
    const jobData = app.job || jobs?.find(j => j.id === app.jobId || j.id === app.job_id);
    return {
      ...app,
      job: jobData,
      // Normalize field names
      appliedAt: app.appliedAt || app.applied_at,
      coverLetter: app.coverLetter || app.cover_letter,
      resumeUrl: app.resumeUrl || app.resume_url
    };
  }).filter(app => app.job);

  // Filter applications
  const filteredApplications = applicationsWithJobs.filter(app => {
    const matchesSearch =
      app.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.job?.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats - ONLY from userApplications
  const stats = {
    total: userApplications.length,
    pending: userApplications.filter(a => a.status === 'pending').length,
    reviewed: userApplications.filter(a => a.status === 'reviewed' || a.status === 'shortlisted').length,
    accepted: userApplications.filter(a => a.status === 'accepted' || a.status === 'hired').length,
    rejected: userApplications.filter(a => a.status === 'rejected').length
  };

  const handleViewDetails = (app: any) => {
    setSelectedApplication(app);
    setDetailsDialogOpen(true);
  };

  const handleDownloadResume = () => {
    if (selectedApplication?.resumeUrl) {
      window.open(selectedApplication.resumeUrl, '_blank');
      toast.success('Opening resume...');
    } else {
      toast.error('Resume not available');
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: any = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: Clock,
        label: 'Pending'
      },
      reviewed: {
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: Eye,
        label: 'Reviewed'
      },
      shortlisted: {
        color: 'bg-purple-100 text-purple-800 border-purple-300',
        icon: CheckCircle2,
        label: 'Shortlisted'
      },
      interviewed: {
        color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
        icon: Eye,
        label: 'Interviewed'
      },
      accepted: {
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircle2,
        label: 'Accepted'
      },
      hired: {
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircle2,
        label: 'Hired'
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: XCircle,
        label: 'Rejected'
      }
    };
    return configs[status] || configs.pending;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Applications</h2>
          <p className="text-gray-600 mt-1">Track your job application status</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {stats.total} Total
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-all ${
            filterStatus === 'pending' ? 'ring-2 ring-yellow-500' : ''
          }`}
          onClick={() => setFilterStatus(filterStatus === 'pending' ? 'all' : 'pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            filterStatus === 'reviewed' ? 'ring-2 ring-blue-500' : ''
          }`}
          onClick={() => setFilterStatus(filterStatus === 'reviewed' ? 'all' : 'reviewed')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Reviewed</p>
                <p className="text-2xl font-bold">{stats.reviewed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            filterStatus === 'accepted' ? 'ring-2 ring-green-500' : ''
          }`}
          onClick={() => setFilterStatus(filterStatus === 'accepted' ? 'all' : 'accepted')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Accepted</p>
                <p className="text-2xl font-bold">{stats.accepted}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            filterStatus === 'rejected' ? 'ring-2 ring-red-500' : ''
          }`}
          onClick={() => setFilterStatus(filterStatus === 'rejected' ? 'all' : 'rejected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-2xl font-bold">{stats.rejected}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search applications..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('all')}
            >
              All
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' ? 'No applications found' : 'No applications yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Start applying to jobs to see them here'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button onClick={() => navigate('/jobseeker-dashboard/jobs')}>
                Browse Jobs
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredApplications.map((app) => {
            const statusConfig = getStatusConfig(app.status);
            const StatusIcon = statusConfig.icon;

            return (
              <Card key={app.id} className="hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Job Info */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-7 w-7 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-xl text-gray-900">
                          {app.job?.title}
                        </h3>
                        <p className="text-gray-600 text-lg">{app.job?.company}</p>
                        <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>Applied: {new Date(app.appliedAt).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            <span>{app.job?.location || 'Remote'}</span>
                          </div>
                          {app.job?.salary && (
                            <div className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4" />
                              <span>{app.job.salary}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Briefcase className="h-4 w-4" />
                            <span>{app.job?.type || 'Full-time'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status and Actions */}
                    <div className="flex flex-col items-end gap-3">
                      <Badge className={`${statusConfig.color} border px-4 py-2`}>
                        <StatusIcon className="h-4 w-4 mr-2" />
                        {statusConfig.label}
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(app)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/jobs/${app.job?.id}`)}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Job Post
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Results Footer */}
      {filteredApplications.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-center text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredApplications.length}</span> of{' '}
              <span className="font-semibold">{applicationsWithJobs.length}</span> applications
            </div>
          </CardContent>
        </Card>
      )}

      {/* Application Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Application Details
            </DialogTitle>
            <DialogDescription>
              View your complete application information
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-6">
              {/* Job Information */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Job Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Position</p>
                      <p className="font-semibold">{selectedApplication.job?.title}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-semibold">{selectedApplication.job?.company}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-semibold">{selectedApplication.job?.location || 'Remote'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Job Type</p>
                      <p className="font-semibold">{selectedApplication.job?.type || 'Full-time'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Application Status */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Application Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Current Status</p>
                      <Badge className={`${getStatusConfig(selectedApplication.status).color} border px-3 py-1 mt-1`}>
                        {getStatusConfig(selectedApplication.status).label}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Applied Date</p>
                      <p className="font-semibold">
                        {new Date(selectedApplication.appliedAt || selectedApplication.applied_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Your Information */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <UserCircle className="h-5 w-5" />
                    Your Information
                  </h3>
                  <div className="space-y-3">
                    {currentUser?.name && (
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="font-semibold">{currentUser.name}</span>
                      </div>
                    )}
                    {currentUser?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="font-semibold">{currentUser.email}</span>
                      </div>
                    )}
                    {currentUser?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="font-semibold">{currentUser.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Cover Letter */}
              {selectedApplication.coverLetter && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Cover Letter
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedApplication.coverLetter}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Resume */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Resume
                  </h3>
                  <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="font-semibold">Your Resume</p>
                        <p className="text-sm text-gray-600">
                          {selectedApplication.resumeUrl ? 'Resume attached' : 'No resume attached'}
                        </p>
                      </div>
                    </div>
                    {selectedApplication.resumeUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadResume}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        View Resume
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Notes from Employer */}
              {selectedApplication.notes && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Notes from Employer
                    </h3>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-gray-700">{selectedApplication.notes}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              navigate(`/jobs/${selectedApplication?.job?.id}`);
              setDetailsDialogOpen(false);
            }}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Job Post
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
